import Phaser from 'phaser';
import type { GameState } from '@/ui/store/gameStore';
import type {
  WorldInteractionController,
  WorldInteractionOptions,
} from './WorldInteractionController';

type WorldInteractionUiState = Pick<
  GameState,
  'openModal' | 'hatchPhase' | 'trade' | 'isTeleporting'
>;

/** Jeden kontrakt blokowania dotykanych punktów świata, wspólny dla scen. */
export function canUseWorldInteraction(state: WorldInteractionUiState): boolean {
  return (
    state.openModal === null &&
    state.hatchPhase === null &&
    state.trade === null &&
    !state.isTeleporting
  );
}

/**
 * Wspólny marker interakcji ze stałym obiektem świata.
 *
 * Ikona dłoni zawsze oznacza „przytrzymaj / kliknij”. Żółty badge `!` jest osobnym
 * sygnałem dostępnej nagrody lub akcji i może być włączany niezależnie. Dzięki
 * temu cooldown nie sprawia, że nadal interaktywny obiekt wygląda jak dekoracja.
 */
export class WorldInteractionMarker {
  readonly container: Phaser.GameObjects.Container;

  private readonly attentionBadge: Phaser.GameObjects.Arc;
  private readonly attentionBang: Phaser.GameObjects.Text;
  private readonly pulse: Phaser.Tweens.Tween;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    onPress: () => void,
    interactions: WorldInteractionController,
    interactionOptions: WorldInteractionOptions = {},
    depth = 100_210,
  ) {
    this.container = scene.add.container(x, y).setDepth(depth);

    const background = scene.add
      .circle(0, 0, 31, 0xffffff, 0.94)
      .setStrokeStyle(3, 0x19a7a5, 1)
      .setInteractive({ useHandCursor: true });

    const icon = scene.textures.exists('ui_interact_tap')
      ? scene.add.image(0, 1, 'ui_interact_tap').setDisplaySize(52, 52)
      : scene.add.text(0, 1, '!', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '36px',
          fontStyle: 'bold',
          color: '#19a7a5',
        }).setOrigin(0.5);

    this.attentionBadge = scene.add
      .circle(23, -23, 12, 0xffd34d, 1)
      .setStrokeStyle(3, 0xffffff, 1)
      .setVisible(false);
    this.attentionBang = scene.add.text(23, -23, '!', {
      fontFamily: 'Baloo 2, system-ui, sans-serif',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#7a3d00',
    }).setOrigin(0.5).setVisible(false);

    this.container.add([background, icon, this.attentionBadge, this.attentionBang]);

    interactions.bind(background, onPress, interactionOptions);

    this.pulse = scene.tweens.add({
      targets: this.container,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Pokazuje lub ukrywa dodatkowe `!`; sama ikona dotyku pozostaje widoczna. */
  setAttention(visible: boolean): void {
    this.attentionBadge.setVisible(visible);
    this.attentionBang.setVisible(visible);
  }

  destroy(): void {
    this.pulse.stop();
    this.container.destroy(true);
  }
}
